import Axios from "axios";
import { CommonTexts as Texts } from "../helpers/LayoutTexts";
import { TeacherSearchResponseModel } from "../models/TeacherSearchResponseModel";
import { buildWebStorage, setupCache } from "axios-cache-interceptor";
import { LoginModel } from "../models/LoginModel";
import { RegisterModel } from "../models/RegisterModel";
import { EmailPasswordModel } from "../models/EmailPasswordModel";
import { getLanguageCode } from "../helpers/Common";

const instance = Axios.create();
const axios = setupCache(instance, { storage: buildWebStorage(localStorage, 'axios-cache:') });

export async function postTeacherLogin(loginModel: LoginModel) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/login", loginModel)
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    })
}

export async function postTeacherLogout(teacherId: number, token: string) {    
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/logout?teacherId=" + teacherId, {}, {
            headers: {
                'Authorization' : `Bearer ${token}`
            }
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postTeacherRegistration(registerModel: RegisterModel) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/register", registerModel)
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    })
}

export async function postLinkedInAuthCode(authCode: string){
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/authenticate-with-linked-in?authCode=" + authCode)
        .then(response => {
            resolve(response.data);
        })
        .catch(error => {
            reject(error)
        })
    })
}

export async function postLostPasswordRequest(email: string) {    
    return new Promise<any>((resolve, reject) => {
        axios        
        .post(Texts.apiBaseUrl + "teachers/lost-password?email=" + email, {})
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getResetPasswordValidity(email: string, token: string) {    
    return new Promise<any>((resolve, reject) => {
        axios        
        .get(Texts.apiBaseUrl + "teachers/reset-password-validity", {            
            params: {      
                email: email,
                token: token
            },
            cache: false
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postResetPassword(passwordModel: EmailPasswordModel) {    
    return new Promise<any>((resolve, reject) => {
        axios 
        .post(Texts.apiBaseUrl + "teachers/reset-password", passwordModel)
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getAdRequestModel(gradeCategoryIds?: string, districtIds?: string) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/ad-request-model", {            
            params: {
                gradeCategoryIds: gradeCategoryIds ? gradeCategoryIds : "",
                districtIds: districtIds ? districtIds : "",
                languageCode: getLanguageCode()
            },            
            cache: {                
                ttl: 1000 * 60 * 60 * 24 // 1 day
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postAdSearchModel(responseModel: TeacherSearchResponseModel) {
    responseModel.LanguageCode = getLanguageCode();
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/ad-search-model", responseModel, {                                
            cache: {    
                methods: ["post"],            
                ttl: 1000 * 60 * 60 // 1 hour
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getGradeCategorySubjects(gradeCategoryIds?: string) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/grade-category-subjects", {            
            params: {
                gradeCategoryIds: gradeCategoryIds ? gradeCategoryIds : "",
                languageCode: getLanguageCode()
            },            
            cache: {                
                ttl: 1000 * 60 * 60 * 24 // 1 day
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getDistrictTowns(districtIds?: string) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/district-towns", {            
            params: {
                districtIds: districtIds ? districtIds : "",
                languageCode: getLanguageCode()
            },            
            cache: {                
                ttl: 1000 * 60 * 60 * 24 * 30 // 30 days
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postAdResponseModel(formData: FormData) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/ad-response", formData, {
            headers: {                
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postAdEditModel(formData: FormData) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/ad-edit-response", formData, {
            headers: {                
                'Content-Type': 'multipart/form-data'
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getTeacherAds(teacherId: number, status: string) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/teacher-ads", {            
            params: {
                teacherId: teacherId,
                status: status,
                languageCode: getLanguageCode()
            },
            cache: false
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getTeacherAdDetails(teacherAdId: number) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/ad-with-details", {            
            params: {
                teacherAdId: teacherAdId,
                languageCode: getLanguageCode()
            },
            cache: {                
                ttl: 1000 * 60 * 60 * 24 // 1 day
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getTeacherProfile(teacherId: number) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/profile", {            
            params: {
                teacherId: teacherId
            },
            cache: false
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function postTeacherProfile(formData: FormData) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/profile", formData)
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function deleteTeacherAd(deleteAdId: number) {
    return new Promise<any>((resolve, reject) => {
        axios
        .delete(Texts.apiBaseUrl + "teachers/delete-ad", {            
            params: {
                deleteAdId: deleteAdId
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function validatePromoCode(teacherId: number, promoCode: string | undefined, paymentTypeId: number) {    
    return new Promise<any>((resolve, reject) => {
        axios 
        .get(Texts.apiBaseUrl + "teachers/promo-code-validation", {            
            params: {      
                teacherId: teacherId, 
                promoCode: promoCode, 
                paymentTypeId: paymentTypeId
            },
            cache: false
        })
        .then((response) => {
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    })
}

export async function getTeacherFeaturedAds() {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/featured-ads", {                      
            cache: {                
                ttl: 1000 * 60 * 60 * 24 // 1 day
            }
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function validateCashCode(paymentId: number, cashCode: string, whatsappNo: string) {
    return new Promise<any>((resolve, reject) => {
        axios
        .post(Texts.apiBaseUrl + "teachers/validate-cash-code?paymentId=" + paymentId + "&cashCode=" + cashCode + "&whatsappNo=" + whatsappNo)
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}

export async function getTeacherAdsCount(teacherId: number) {
    return new Promise<any>((resolve, reject) => {
        axios
        .get(Texts.apiBaseUrl + "teachers/ads-count-by-teacher", {            
            params: {      
                teacherId: teacherId
            },
            cache: false
        })
        .then((response) => {                
            resolve(response.data);
        })
        .catch((error) => {
            reject(error);
        })
    });
}